import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import { findAvailableLotMatch } from '../../utils/lotIdentity'
import { filterInteractiveLots } from '../../utils/lotInteraction'

export type AvailableLotLike = {
  id: string
  lotKey: string
  stage?: string
  /**
   * Findings #200: true for a lot record that sits in no unit (the arrival
   * record of "Sembrar en varias unidades"). Rows are never matched to it by
   * name. Unknown (older cached rosters): treated as a unit lot.
   */
  unitless?: boolean
}

type ProductionRowLotLike = {
  lot?: unknown
  lotId?: unknown
  unmappedLot?: unknown
}

export type ProductionRowLotResolution = {
  /** Whether any of `lot` / `lotId` / `unmappedLot` should change. */
  changed: boolean
  /** New field values — only meaningful when `changed` is true. */
  fields: { lot: string; lotId: string; unmappedLot: string }
  /**
   * The row's lotId/label could not be resolved to any real lot (farm-wide) and
   * was moved to `unmappedLot` — this is the "Sin mapear" recovery case.
   */
  cleared: boolean
  /**
   * The row's lotId resolves to a real lot that lives in a DIFFERENT production
   * unit now — it was transferred out after producing history here. Its rows
   * stay visible and correctly labelled (no "Sin mapear", no recovery buttons),
   * read-only because the lot is managed elsewhere. Track A finding 4.2.
   */
  isForeignLot: boolean
  /**
   * Findings #200: the row belongs to a deleted lot. It is left exactly as it
   * is (never re-linked to another lot by name) and the screen hides it.
   */
  isDeletedLot?: boolean
}

const buildLotKeyById = (lots: ReadonlyArray<AvailableLotLike>): Map<string, string> =>
  new Map(lots.map((lot) => [String(lot.id), String(lot.lotKey ?? '')] as const))

const unchanged = (isForeignLot: boolean): ProductionRowLotResolution => ({
  changed: false,
  fields: { lot: '', lotId: '', unmappedLot: '' },
  cleared: false,
  isForeignLot
})

const resolveTo = (
  fields: { lot: string; lotId: string; unmappedLot: string },
  opts: { cleared?: boolean; isForeignLot?: boolean } = {}
): ProductionRowLotResolution => ({
  changed: true,
  fields,
  cleared: Boolean(opts.cleared),
  isForeignLot: Boolean(opts.isForeignLot)
})

/**
 * Per-row lot reconciliation for the Datatable "safe migration" pass. Given the
 * production unit's own lot roster AND the farm-wide roster, decide what a row's
 * `lot` / `lotId` / `unmappedLot` should be.
 *
 * The key case (Track A finding 4.2): a row whose `lotId` is NOT in this
 * production unit's roster is only genuinely orphaned when it is also unknown
 * FARM-WIDE. A lotId that resolves to a real lot elsewhere on the farm belongs
 * to a lot that was transferred out of this unit — its history here is real and
 * must stay visible, not be relabelled "Sin mapear".
 *
 * Returns a builder so the id→label maps are built once per pass, not per row.
 */
export const buildProductionRowLotReconciler = (
  productionUnitLots: ReadonlyArray<AvailableLotLike>,
  farmLots: ReadonlyArray<AvailableLotLike>,
  options: { isDeletedLotRow?: (row: ProductionRowLotLike) => boolean } = {}
): ((row: ProductionRowLotLike) => ProductionRowLotResolution) => {
  const puLotKeyById = buildLotKeyById(productionUnitLots)
  const farmLotKeyById = buildLotKeyById(farmLots)
  // Findings #200: a deleted lot's rows used to be cleared and then matched by
  // name to the farm's arrival record (no unit), showing "(transferido)" and
  // saving that link. Name matching only considers lots that sit in a unit.
  const farmLotsInUnits = farmLots.filter((lot) => lot.unitless !== true)

  return (row) => {
    if (options.isDeletedLotRow?.(row)) {
      return { ...unchanged(false), isDeletedLot: true }
    }

    const existingLotId = String(row.lotId ?? '').trim()
    const existingLotLabel = String(row.lot ?? '').trim()
    const existingUnmapped = String(row.unmappedLot ?? '').trim()

    if (existingLotId) {
      const puLabel = puLotKeyById.get(existingLotId)
      if (puLabel) {
        if (existingLotLabel !== puLabel) {
          return resolveTo({ lot: puLabel, lotId: existingLotId, unmappedLot: '' })
        }
        if (existingUnmapped) {
          return resolveTo({ lot: existingLotLabel, lotId: existingLotId, unmappedLot: '' })
        }
        return unchanged(false)
      }

      const farmLabel = farmLotKeyById.get(existingLotId)
      if (farmLabel !== undefined) {
        const nextLabel = farmLabel || existingLotLabel
        if (existingLotLabel === nextLabel && !existingUnmapped) {
          return unchanged(true)
        }
        return resolveTo(
          { lot: nextLabel, lotId: existingLotId, unmappedLot: '' },
          { isForeignLot: true }
        )
      }

      return resolveTo(
        { lot: '', lotId: '', unmappedLot: existingUnmapped || existingLotLabel || '' },
        { cleared: true }
      )
    }

    if (!existingLotLabel) {
      if (!existingUnmapped) return unchanged(false)

      const puMatch = findAvailableLotMatch(existingUnmapped, productionUnitLots)
      if (puMatch.status === 'matched') {
        const label = puLotKeyById.get(puMatch.match.id) ?? puMatch.match.lotKey
        return resolveTo({ lot: label, lotId: puMatch.match.id, unmappedLot: '' })
      }

      const farmMatch = findAvailableLotMatch(existingUnmapped, farmLotsInUnits)
      if (farmMatch.status === 'matched') {
        const label = farmLotKeyById.get(farmMatch.match.id) ?? farmMatch.match.lotKey
        return resolveTo(
          { lot: label, lotId: farmMatch.match.id, unmappedLot: '' },
          { isForeignLot: true }
        )
      }

      return unchanged(false)
    }

    const puMatch = findAvailableLotMatch(existingLotLabel, productionUnitLots)
    if (puMatch.status === 'matched') {
      const label = puLotKeyById.get(puMatch.match.id) ?? puMatch.match.lotKey
      return resolveTo({ lot: label, lotId: puMatch.match.id, unmappedLot: '' })
    }

    const farmMatch = findAvailableLotMatch(existingLotLabel, farmLotsInUnits)
    if (farmMatch.status === 'matched') {
      const label = farmLotKeyById.get(farmMatch.match.id) ?? farmMatch.match.lotKey
      return resolveTo(
        { lot: label, lotId: farmMatch.match.id, unmappedLot: '' },
        { isForeignLot: true }
      )
    }

    return resolveTo(
      { lot: '', lotId: '', unmappedLot: existingLotLabel },
      { cleared: true }
    )
  }
}

export type SortDirection = 'asc' | 'desc'

export const computeInsertAtForDailyMortality = (args: {
  weightIndex: number
  fishStockIndex: number
  length: number
}): number => {
  const { weightIndex, fishStockIndex, length } = args
  if (weightIndex >= 0) {
    return weightIndex + 1
  }
  if (fishStockIndex >= 0) {
    return fishStockIndex
  }
  return length
}

export const ensureRowHasColumns = <TRow extends Record<string, unknown>>(
  row: TRow,
  columns: string[]
): TRow => {
  const copy = { ...row }
  for (const col of columns) {
    // Sonar prefers ??=; acceptable here because UI treats null/undefined as empty.
    ;(copy as any)[col] ??= ''
  }
  return copy
}

export const ensureRowsHaveColumns = <TRow extends Record<string, unknown>>(
  rows: TRow[],
  columns: string[]
): TRow[] => {
  if (rows.length === 0) return rows
  return rows.map((row) => ensureRowHasColumns(row, columns))
}

export const compareForSort = (args: {
  aValue: unknown
  bValue: unknown
  direction: SortDirection
}): number => {
  const { aValue, bValue, direction } = args

  const aRaw: string | number =
    typeof aValue === 'string' || typeof aValue === 'number' ? aValue : ''
  const bRaw: string | number =
    typeof bValue === 'string' || typeof bValue === 'number' ? bValue : ''

  // Detect ISO-8601 date strings (e.g. "2026-06-25" or "2026-06-25T00:00:00.000Z")
  // BEFORE the numeric branch — otherwise parseFloat would only see the leading
  // year and same-year rows would compare equal. Surfaced as v2.1 finding #45
  // (Database + Water date sort).
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}(?:[T ]|$)/
  const aIsIsoDate = typeof aRaw === 'string' && isoDatePattern.test(aRaw)
  const bIsIsoDate = typeof bRaw === 'string' && isoDatePattern.test(bRaw)
  if (aIsIsoDate && bIsIsoDate) {
    const aTime = Date.parse(aRaw)
    const bTime = Date.parse(bRaw)
    if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
      return direction === 'asc' ? aTime - bTime : bTime - aTime
    }
  }

  const aNum = Number.parseFloat(String(aRaw))
  const bNum = Number.parseFloat(String(bRaw))

  const aNumOk = !Number.isNaN(aNum)
  const bNumOk = !Number.isNaN(bNum)

  if (aNumOk && bNumOk) {
    return direction === 'asc' ? aNum - bNum : bNum - aNum
  }

  const aStr = String(aRaw)
  const bStr = String(bRaw)
  return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
}

export const escapeCsvCell = (value: unknown): string => {
  let s = ''
  if (typeof value === 'string') s = value
  else if (typeof value === 'number') s = Number.isFinite(value) ? String(value) : ''
  else if (typeof value === 'boolean') s = value ? 'true' : 'false'
  if (/[",\n]/.test(s)) {
    s = '"' + s.replaceAll('"', '""') + '"'
  }
  return s
}

export const downloadTextBlob = (args: {
  text: string
  filename: string
  mimeType?: string
}): void => {
  const { text, filename, mimeType } = args
  const blob = new Blob([text], { type: mimeType ?? 'text/plain;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const normalizeStage = (value: string): string => {
  const v = value.trim().toLowerCase()
  return v === 'inactive' ? 'Inactive' : 'Active'
}

export const normalizeHealth = (value: string): string => {
  const v = value.trim().toLowerCase()
  if (v === 'issue') return 'Issue'
  if (v === 'emergency') return 'Emergency'
  return 'Good'
}

export const normalizeTreatmentMort = (value: string): string => {
  const v = value.trim().toLowerCase()
  const opts = ['vaccine', 'antibiotic', 'disinfectant', 'salt', 'other']
  if (opts.includes(v)) return v.charAt(0).toUpperCase() + v.slice(1)
  return value ? String(value) : ''
}

export const normalizeDailyMortality = (value: string): string => {
  const raw = value.trim()
  if (raw === '') return ''
  const numeric = Number.parseInt(raw.replaceAll(/[^0-9-]/g, ''), 10)
  if (Number.isNaN(numeric)) return ''
  return Math.max(0, numeric).toString()
}

export const splitNonEmptyLines = (text: string): string[] => {
  return (text ?? '').split(/\r?\n/).filter(Boolean)
}

export const trimCsvValues = (values: string[]): string[] => {
  return values.map((v) => (v ?? '').trim())
}

export const ensureImportHasAllColumns = (obj: Record<string, unknown>, activeColumns: string[]) => {
  activeColumns.forEach((c) => {
    if ((obj as any)[c] === undefined) (obj as any)[c] = ''
  })
}

export const normalizeImportedMappedValue = (mapped: string, value: string): string => {
  if (mapped === 'stage') return normalizeStage(value)
  if (mapped === 'health') return normalizeHealth(value)
  if (mapped === 'mort') return normalizeTreatmentMort(value)
  return value
}

export const normalizeImportedRowDate = (obj: Record<string, unknown>) => {
  if (typeof (obj as any).date !== 'string') return
  const normalized = normalizeToIsoZMidnight(String((obj as any).date))
  if (normalized.ok) (obj as any).date = normalized.isoZ
}

// U4 (docs/lot-lifecycle-vs-concerns-audit.md §3, §5 PR-α1): extends invariant
// I-3 ("Tier B cell edit is blocked when the paired Tier A stream is
// authoritative") to the Datatable `lot` cell. Re-attributing an
// already-correctly-linked ProductionRecord row to a different lot from this
// rollup screen would orphan the Tier A events (MortalityLog/WeightSampleLog/
// FishStockLog/FeedLog/WaterLog/HarvestLog) still pointing at the row's
// original lot. The one legitimate exception is the unmapped-label recovery
// flow: a label that was captured (e.g. via CSV import) but never resolved to
// any Lot id in the first place — resolving it doesn't re-attribute a linked
// event, it only finishes an attribution that never happened. That flow is
// signaled by `row.unmappedLot` being non-empty at the time of the edit.
export const isLotCellRecoveryEdit = (currentRow: { unmappedLot?: unknown }): boolean => {
  return String(currentRow?.unmappedLot ?? '').trim().length > 0
}

type DatatableImportLot = {
  id: string
  lotKey: string
  stage?: string
}

export const reconcileImportedRowLot = (args: {
  obj: Record<string, unknown>
  availableLots: DatatableImportLot[]
  lotKeyById: Map<string, string>
}) => {
  const incomingLotId = String((args.obj as any).lotId ?? '').trim()
  const incomingLotLabel = String((args.obj as any).lot ?? '').trim()
  const interactiveLots = filterInteractiveLots(args.availableLots)
  const interactiveLotKeyById = new Map(
    interactiveLots.map((lot) => [String(lot.id), String(lot.lotKey ?? '')] as const)
  )

  if (incomingLotId) {
    const mappedLotLabel = interactiveLotKeyById.get(incomingLotId)
    if (mappedLotLabel) {
      ;(args.obj as any).lot = mappedLotLabel
      ;(args.obj as any).unmappedLot = ''
      return
    }

    const resolvedLotFromId = interactiveLots.length > 0
      ? findAvailableLotMatch(incomingLotId, interactiveLots)
      : { status: 'unmatched' as const }

    if (resolvedLotFromId.status === 'matched') {
      ;(args.obj as any).lotId = resolvedLotFromId.match.id
      ;(args.obj as any).lot = interactiveLotKeyById.get(resolvedLotFromId.match.id) ?? resolvedLotFromId.match.lotKey
      ;(args.obj as any).unmappedLot = ''
      return
    }

    if (!incomingLotLabel) {
      const inactiveLotLabel = args.lotKeyById.get(incomingLotId) ?? incomingLotId
      ;(args.obj as any).lotId = ''
      ;(args.obj as any).unmappedLot = inactiveLotLabel
      ;(args.obj as any).lot = ''
      return
    }
  }

  if (!incomingLotLabel) return

  const resolvedLot = interactiveLots.length > 0
    ? findAvailableLotMatch(incomingLotLabel, interactiveLots)
    : { status: 'unmatched' as const }

  if (resolvedLot.status === 'matched') {
    ;(args.obj as any).lotId = resolvedLot.match.id
    ;(args.obj as any).lot = interactiveLotKeyById.get(resolvedLot.match.id) ?? resolvedLot.match.lotKey
    ;(args.obj as any).unmappedLot = ''
    return
  }

  ;(args.obj as any).lotId = ''
  ;(args.obj as any).unmappedLot = incomingLotLabel
  ;(args.obj as any).lot = ''
}
